import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';
import { useFieldOptional } from './field-context';
import { controlBaseStyles } from './input';
import { cn } from './utils';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Mostra "N/máx" e anuncia o excedente. Requer `maxLength`. */
  readonly showCounter?: boolean;
  readonly value?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, rows = 4, showCounter = false, maxLength, value, ...props },
  ref,
) {
  const field = useFieldOptional();
  const counterId = useId();
  const length = typeof value === 'string' ? value.length : 0;
  const over = typeof maxLength === 'number' && length > maxLength;

  const describedBy = [field?.controlProps['aria-describedby'], showCounter ? counterId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1">
      <textarea
        ref={ref}
        rows={rows}
        maxLength={maxLength}
        value={value}
        {...field?.controlProps}
        aria-describedby={describedBy || undefined}
        {...props}
        className={cn(controlBaseStyles, 'min-h-[5rem] resize-y px-3 py-2 text-base', className)}
      />
      {showCounter && typeof maxLength === 'number' ? (
        /*
          `aria-live="polite"`: o contador muda a cada tecla; anunciar de forma
          assertiva atrapalharia a digitação. Polite avisa quando o usuário pausa.
        */
        <p
          id={counterId}
          aria-live="polite"
          className={cn(
            'self-end text-2xs qc-tabular',
            over ? 'font-semibold text-danger-content' : 'text-content-subtle',
          )}
        >
          {length} de {maxLength} caracteres
        </p>
      ) : null}
    </div>
  );
});
